import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, KeyRound, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { BrandLogo } from '../../components/BrandLogo';
import { ThemeToggle } from '../../components/ThemeToggle';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { InlineSpinner } from '../../components/LoadingIndicators';
import type { OrganizationInvitation } from '../../types/domain';

export function InvitationAcceptancePage() {
  const { token = '' } = useParams();
  const { signOut } = useAuth();
  const [invitation, setInvitation] = useState<OrganizationInvitation | null>(null);
  const [inspectionError, setInspectionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void apiRequest<OrganizationInvitation>('/auth/invitations/inspect/', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
      .then((result) => { if (active) setInvitation(result); })
      .catch((error: unknown) => { if (active) setInspectionError(getApiErrorMessage(error)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const acceptance = useMutation({
    mutationFn: () => apiRequest<{ detail: string; email: string }>('/auth/invitations/accept/', {
      method: 'POST',
      body: JSON.stringify({ token, full_name: fullName, password }),
    }),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setLocalError(null);
    if (password !== confirmation) {
      setLocalError('Passwords do not match.');
      return;
    }
    acceptance.mutate();
  }

  if (loading) return <InvitationFrame><div className="invitation-card data-state"><span className="inline-loader" /> Checking invitation…</div></InvitationFrame>;
  if (inspectionError || !invitation) {
    return <InvitationMessage title="Invitation unavailable" message={inspectionError ?? 'This invitation link is invalid.'} />;
  }
  if (acceptance.isSuccess) {
    return (
      <InvitationMessage
        success
        title="Your account is ready"
        message={`You can now sign in as ${acceptance.data.email}.`}
        onSignIn={signOut}
      />
    );
  }
  if (invitation.status !== 'pending') {
    return <InvitationMessage title={`Invitation ${invitation.status}`} message="Ask your organisation administrator to send a new invitation." />;
  }

  return (
    <InvitationFrame>
      <section className="invitation-card">
        <BrandLogo className="brand-logo-invitation" />
        <p className="eyebrow">Account invitation</p>
        <h1>Join {invitation.organization_name}</h1>
        <p className="invitation-summary">You were invited as <strong>{invitation.role.replaceAll('_', ' ')}</strong> using {invitation.email}.</p>
        <form className="invitation-form" onSubmit={submit}>
          <label>
            Full name
            <input autoComplete="name" required value={fullName} onChange={(event) => setFullName(event.target.value)} />
          </label>
          <label>
            Password
            <input autoComplete="new-password" minLength={8} required type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
          </label>
          <label>
            Confirm password
            <input autoComplete="new-password" minLength={8} required type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
          </label>
          {localError || acceptance.isError ? <p className="form-error" role="alert">{localError ?? getApiErrorMessage(acceptance.error)}</p> : null}
          <button className="button primary full" disabled={acceptance.isPending} type="submit">
            {acceptance.isPending ? <InlineSpinner /> : <KeyRound size={18} />} {acceptance.isPending ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <p className="security-note"><ShieldCheck size={15} /> Your invitation can be used only once.</p>
      </section>
    </InvitationFrame>
  );
}

function InvitationMessage({
  title,
  message,
  success = false,
  onSignIn,
}: {
  title: string;
  message: string;
  success?: boolean;
  onSignIn?: () => void;
}) {
  return (
    <InvitationFrame>
      <section className="invitation-card invitation-result">
        {success ? <CheckCircle2 size={42} /> : <KeyRound size={42} />}
        <h1>{title}</h1>
        <p>{message}</p>
        <Link className="button primary" onClick={onSignIn} to="/">Go to sign in</Link>
      </section>
    </InvitationFrame>
  );
}

function InvitationFrame({ children }: { children: ReactNode }) {
  return (
    <div className="invitation-page">
      <ThemeToggle className="auth-theme-toggle" />
      {children}
    </div>
  );
}
